import React, { useState } from "react";

import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import "pages/AdminPage.scss";

interface PointHistory {
  id: string;
  customer: string;
  type: "earn" | "use" | "expire" | "admin_add";
  amount: number;
  balance: number;
  description: string;
  createdAt: string;
}

const TYPE_LABEL: Record<string, string> = {
  earn: "적립",
  use: "사용",
  expire: "만료",
  admin_add: "관리자 지급",
};
const TYPE_BADGE: Record<string, "active" | "info" | "draft" | "pending"> = {
  earn: "active",
  use: "info",
  expire: "draft",
  admin_add: "pending",
};

const MOCK: PointHistory[] = [
  {
    id: "1",
    customer: "김민지",
    type: "earn",
    amount: 1280,
    balance: 5680,
    description: "주문 적립 (ORD-20240301-001)",
    createdAt: "2024-03-01",
  },
  {
    id: "2",
    customer: "이준영",
    type: "use",
    amount: -2000,
    balance: 3000,
    description: "포인트 사용 (ORD-20240301-002)",
    createdAt: "2024-03-01",
  },
  {
    id: "3",
    customer: "박소연",
    type: "admin_add",
    amount: 5000,
    balance: 8500,
    description: "이벤트 당첨 지급",
    createdAt: "2024-03-02",
  },
  {
    id: "4",
    customer: "최건우",
    type: "expire",
    amount: -1500,
    balance: 0,
    description: "유효기간 만료",
    createdAt: "2024-03-02",
  },
  {
    id: "5",
    customer: "정유진",
    type: "earn",
    amount: 450,
    balance: 2350,
    description: "리뷰 작성 적립",
    createdAt: "2024-03-03",
  },
  {
    id: "6",
    customer: "강현수",
    type: "use",
    amount: -3000,
    balance: 4200,
    description: "포인트 사용 (ORD-20240303-002)",
    createdAt: "2024-03-03",
  },
];

const TABS: TabItem[] = [
  { key: "all", label: "전체" },
  { key: "earn", label: "적립" },
  { key: "use", label: "사용" },
  { key: "admin_add", label: "관리자 지급" },
  { key: "expire", label: "만료" },
];

const PointManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ customer: "", amount: "", description: "" });

  const filtered = MOCK.filter((p) => activeTab === "all" || p.type === activeTab);

  const columns: Column<PointHistory>[] = [
    { key: "customer", title: "고객명", width: "90px" },
    {
      key: "type",
      title: "유형",
      width: "100px",
      render: (v) => <Badge variant={TYPE_BADGE[v]}>{TYPE_LABEL[v]}</Badge>,
    },
    {
      key: "amount",
      title: "포인트",
      width: "100px",
      render: (v) => (
        <span style={{ color: v > 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>
          {v > 0 ? "+" : ""}
          {v.toLocaleString()}P
        </span>
      ),
    },
    {
      key: "balance",
      title: "잔여 포인트",
      width: "110px",
      render: (v) => `${v.toLocaleString()}P`,
    },
    { key: "description", title: "내용" },
    { key: "createdAt", title: "일시", width: "110px" },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="포인트 관리"
        description="고객 포인트 내역을 조회하고 관리합니다."
        actions={[
          <Button key="add" variant="primary" onClick={() => setShowModal(true)}>
            포인트 지급
          </Button>,
        ]}
      />

      <div className="stat-cards">
        <div className="stat-card stat-card--primary">
          <div className="stat-card__label">총 발행 포인트</div>
          <div className="stat-card__value">1,284,500P</div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card__label">사용된 포인트</div>
          <div className="stat-card__value">856,200P</div>
        </div>
        <div className="stat-card stat-card--warning">
          <div className="stat-card__label">잔여 포인트</div>
          <div className="stat-card__value">428,300P</div>
        </div>
      </div>

      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setCurrentPage(1);
          }}
        />
        <div className="admin-page__filter">
          <Input placeholder="고객명 검색" style={{ width: 240 }} prefix="🔍" />
        </div>
        <Table columns={columns} data={filtered} rowKey="id" />
        <Pagination current={currentPage} total={filtered.length} onChange={setCurrentPage} />
      </div>

      <Modal
        open={showModal}
        title="포인트 지급"
        onClose={() => setShowModal(false)}
        width="400px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              지급
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input label="고객명 또는 이메일" placeholder="고객 검색" value={form.customer} onChange={(e) => setForm((p) => ({ ...p, customer: e.target.value }))} />
          <Input label="지급 포인트" type="number" placeholder="포인트 입력" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>지급 사유</label>
            <textarea
              rows={3}
              placeholder="포인트 지급 사유를 입력해주세요."
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
                resize: "none",
                outline: "none",
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PointManagement;
